/**
 * True when a route error is TanStack Query's `CancelledError` — a prefetch
 * that was aborted, not a fault.
 *
 * How it reaches the router: a loader awaits `ensureQueryData` on a query
 * that a mounted component ALSO observes (the router re-rendered a cached
 * match while re-running its loader — stale-while-revalidate). When that
 * component unmounts mid-fetch it is the query's last observer, so TanStack
 * Query aborts the shared request and the loader's promise rejects with
 * `CancelledError`. Nothing is wrong with the server or the data; the right
 * response is to re-run the loader, not to show « Une erreur est survenue ».
 *
 * Duck-typed rather than `instanceof` so it survives a duplicated
 * `@tanstack/query-core` in the bundle. The class sets `message` to
 * "CancelledError" (its `name` stays "Error") and always defines the
 * `revert` / `silent` own-properties, even as `undefined`. Pure; exported for
 * tests.
 */
export function isCancelledRouteError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const e = error as { name?: unknown; message?: unknown };
  return (
    e.name === "CancelledError" ||
    e.message === "CancelledError" ||
    ("revert" in e && "silent" in e)
  );
}
